<template>
  <ModalVue
    :modalActive="modalActive"
    :hasHeaderBorder="true"
    :hasFooterBorder="true"
    maxWidth="max-w-[640px]"
    @close="handleClose"
  >
    <template #header>
      <h2 class="text-lg font-bold text-textPrimary text-left w-full">Selecionar escala</h2>
    </template>

    <!-- Body: search + scrollable tree -->
    <div class="flex flex-col gap-4">
      <!-- Search input -->
      <div class="relative">
        <SVGIcon
          :path="mdiMagnify"
          class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
        />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Buscar empresa, unidade ou escala"
          maxlength="120"
          class="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary30 focus:border-primary30"
          data-testid="import-config-search-input"
        />
      </div>

      <!-- Loading (primeira pagina) -->
      <div v-if="isLoading" class="flex justify-center py-8" data-testid="import-config-loading">
        <v-progress-circular indeterminate color="primary" size="32" />
      </div>

      <!-- Erro -->
      <div v-else-if="errorMessage" class="flex flex-col items-center gap-3 py-6">
        <p class="text-sm text-error text-center" data-testid="import-config-error">
          {{ errorMessage }}
        </p>
        <outlined-button
          text="Tentar novamente"
          width="auto"
          height="36px"
          data-testid="import-config-retry-button"
          @click="reload"
        />
      </div>

      <!-- Empty state -->
      <p
        v-else-if="tree.length === 0"
        class="text-sm text-gray-500 text-center py-6"
        data-testid="import-config-empty"
      >
        Nenhuma escala encontrada
      </p>

      <!-- Tree -->
      <div v-else ref="scrollContainer" class="flex flex-col gap-3 max-h-[400px] overflow-y-auto">
        <!-- Company Container -->
        <div v-for="company in tree" :key="company.id" class="rounded-lg bg-[#F0F0F4] p-3">
          <!-- Company row -->
          <div
            class="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
            data-testid="import-config-company-row"
            @click="toggleExpanded('company', company.id)"
          >
            <div class="flex-1">
              <span
                class="block text-xs font-bold text-black text-left"
                style="letter-spacing: 0.06px"
                >Empresa</span
              >
              <p
                class="text-base font-normal text-black leading-6 text-left"
                style="letter-spacing: 0.08px"
              >
                {{ company.name }}
              </p>
            </div>
            <SVGIcon :path="mdiMenuDown" class="w-6 h-6 text-gray-400 shrink-0" />
          </div>

          <!-- Units (collapsible) -->
          <div v-if="isExpanded('company', company.id)" class="flex flex-col gap-3 mt-3">
            <!-- Unit Container -->
            <div v-for="unit in company.units" :key="unit.id" class="rounded-lg bg-white p-3">
              <!-- Unit row -->
              <div
                class="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
                data-testid="import-config-unit-row"
                @click="toggleExpanded('unit', unit.id)"
              >
                <div class="flex-1">
                  <span
                    class="block text-xs font-bold text-black text-left"
                    style="letter-spacing: 0.06px"
                    >Unidade</span
                  >
                  <p
                    class="text-base font-normal text-black leading-6 text-left"
                    style="letter-spacing: 0.08px"
                  >
                    {{ unit.name }}
                  </p>
                </div>
                <SVGIcon :path="mdiMenuDown" class="w-6 h-6 text-gray-400 shrink-0" />
              </div>

              <!-- Schedules (collapsible, radio-selectable) -->
              <div v-if="isExpanded('unit', unit.id)" class="flex flex-col gap-2 mt-3">
                <div
                  v-for="schedule in unit.schedules"
                  :key="schedule.id"
                  class="flex items-start gap-3 p-2 rounded-lg border cursor-pointer transition-colors"
                  :class="
                    selectedScheduleId === schedule.id
                      ? 'bg-primary5 border-primary'
                      : 'bg-[#F0F0F4] border-gray-200 hover:bg-gray-200'
                  "
                  data-testid="import-config-schedule-option"
                  @click="selectedScheduleId = schedule.id"
                >
                  <div
                    class="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5"
                    :class="
                      selectedScheduleId === schedule.id ? 'border-primary' : 'border-gray-300'
                    "
                  >
                    <div
                      v-if="selectedScheduleId === schedule.id"
                      class="w-2.5 h-2.5 rounded-full bg-primary"
                    />
                  </div>
                  <div class="flex-1">
                    <span
                      class="block text-xs font-bold text-black text-left"
                      style="letter-spacing: 0.06px"
                      >Escala</span
                    >
                    <p
                      class="text-base font-normal text-black leading-6 text-left"
                      style="letter-spacing: 0.08px"
                    >
                      {{ schedule.name }}
                    </p>
                  </div>
                </div>

                <!-- Primeira carga das escalas da unidade -->
                <div
                  v-if="unit.isLoading && unit.schedules.length === 0"
                  class="flex justify-center py-2"
                  data-testid="import-config-unit-loading"
                >
                  <v-progress-circular indeterminate color="primary" size="20" />
                </div>

                <!-- Falha isolada nesta unidade: não derruba o resto da árvore -->
                <div
                  v-else-if="unit.error"
                  class="flex flex-col items-center gap-2 py-2"
                  data-testid="import-config-unit-error"
                >
                  <p class="text-xs text-error text-center">{{ unit.error }}</p>
                  <outlined-button
                    text="Tentar novamente"
                    width="auto"
                    height="32px"
                    data-testid="import-config-unit-retry-button"
                    @click="loadUnitSchedules(unit.id, { append: false })"
                  />
                </div>

                <p
                  v-else-if="unit.schedules.length === 0"
                  class="text-xs text-gray-500 text-center py-2"
                  data-testid="import-config-unit-empty"
                >
                  Nenhuma escala nesta unidade
                </p>

                <!-- Sentinela desta unidade: pagina as escalas dela isoladamente -->
                <div
                  v-if="unit.hasMore"
                  :data-load-more="unit.id"
                  class="flex justify-center py-2"
                  data-testid="import-config-unit-load-more-sentinel"
                >
                  <v-progress-circular
                    v-if="unit.isLoading"
                    indeterminate
                    color="primary"
                    size="20"
                    data-testid="import-config-unit-loading-more"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sentinela do resultado de busca (lista plana global) -->
        <div
          v-if="hasMoreSearch"
          data-load-more="search"
          class="flex justify-center py-3"
          data-testid="import-config-load-more-sentinel"
        >
          <v-progress-circular
            v-if="isLoadingMoreSearch"
            indeterminate
            color="primary"
            size="20"
            data-testid="import-config-loading-more"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-between gap-4">
        <!-- Fechar no meio do import deixaria o wizard meio preenchido -->
        <outlined-button
          text="Cancelar"
          width="auto"
          height="44px"
          :disabled="isImporting"
          data-testid="import-config-cancel-button"
          @click="handleClose"
        />
        <filled-button
          :text="confirmText ?? 'Importar configurações'"
          width="auto"
          height="44px"
          :loading="isImporting"
          :disabled="selectedScheduleId === null || isImporting"
          class="bg-primary30"
          data-testid="import-config-confirm-button"
          @click="handleImport"
        />
      </div>
    </template>
  </ModalVue>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'

import { mdiMagnify, mdiMenuDown } from '@mdi/js'

import Network from '@/Network'
import type {
  ImportableScheduleItem,
  ImportableScheduleTreeCompany
} from '@/Network/Types/Responses/ScheduleUnitResponses'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import { groupImportableSchedules } from '@/utils/schedule'
import { getApiErrorMessage } from '@/utils/apiError'

const PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 300

const props = defineProps<{
  modalActive: boolean
  /**
   * O pai controla: fica `true` enquanto o snapshot de configuração está sendo
   * buscado e aplicado no wizard. O modal continua aberto nesse intervalo, então
   * precisa mostrar progresso e bloquear um segundo clique.
   */
  isImporting?: boolean
  /** Texto do botão de confirmar (default "Importar configurações"); "Copiar requisitos" no D42. */
  confirmText?: string
}>()
const emit = defineEmits<{
  close: []
  /** `companyId` da escala escolhida: a cópia de requisitos só guarda a origem na mesma empresa. */
  select: [scheduleId: number, companyId: number | null]
}>()

/** Página de escalas de uma unidade, no modo de navegação. */
type UnitPage = {
  items: { id: number; name: string }[]
  count: number
  isLoading: boolean
  error: string
}

/** Nó de unidade já resolvido para renderização, em qualquer um dos dois modos. */
type RenderUnit = {
  id: number
  name: string
  schedules: { id: number; name: string }[]
  isLoading: boolean
  error: string
  hasMore: boolean
}

type RenderCompany = { id: number; name: string; units: RenderUnit[] }

// Modo navegação: estrutura completa numa chamada + escalas paginadas por unidade.
const treeCompanies = ref<ImportableScheduleTreeCompany[]>([])
const unitPages = ref<Map<number, UnitPage>>(new Map())

// Modo busca: lista plana global, agrupada para render, com sentinela única.
const searchItems = ref<ImportableScheduleItem[]>([])
const searchCount = ref(0)
const isLoadingMoreSearch = ref(false)

const isLoading = ref(false)
const errorMessage = ref('')
const searchQuery = ref('')
const selectedScheduleId = ref<number | null>(null)
const expandedCompanies = ref<Set<number>>(new Set())
const expandedUnits = ref<Set<number>>(new Set())

const scrollContainer = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null
let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined

const isSearching = computed(() => searchQuery.value.trim().length > 0)
const hasMoreSearch = computed(() => searchItems.value.length < searchCount.value)

/**
 * Os dois modos são normalizados na mesma forma de render, então o template não sabe
 * de qual endpoint os dados vieram.
 *
 * - navegação: `treeCompanies` (estrutura) + `unitPages` (escalas por unidade);
 * - busca: resultado plano agrupado, sem paginação por unidade — a sentinela é única.
 */
const tree = computed<RenderCompany[]>(() => {
  if (isSearching.value) {
    return groupImportableSchedules(searchItems.value).map((company) => ({
      id: company.id,
      name: company.name,
      units: company.units.map((unit) => ({
        id: unit.id,
        name: unit.name,
        schedules: unit.schedules,
        isLoading: false,
        error: '',
        hasMore: false
      }))
    }))
  }

  return treeCompanies.value.map((company) => ({
    id: company.id,
    name: company.name,
    units: company.units.map((unit) => {
      const page = unitPages.value.get(unit.id)
      return {
        id: unit.id,
        name: unit.name,
        schedules: page?.items ?? [],
        isLoading: page?.isLoading ?? false,
        error: page?.error ?? '',
        hasMore: page ? page.items.length < page.count : false
      }
    })
  }))
})

function toggleExpanded(level: 'company' | 'unit', id: number) {
  const set = level === 'company' ? expandedCompanies : expandedUnits
  if (set.value.has(id)) {
    set.value.delete(id)
  } else {
    set.value.add(id)
    // No modo navegação as escalas só são buscadas quando a unidade abre.
    if (level === 'unit' && !isSearching.value && !unitPages.value.has(id)) {
      loadUnitSchedules(id, { append: false })
    }
  }
}

function isExpanded(level: 'company' | 'unit', id: number): boolean {
  const set = level === 'company' ? expandedCompanies : expandedUnits
  return set.value.has(id)
}

/**
 * Com a busca no servidor, deixar a árvore colapsada esconderia o resultado atrás
 * dos chevrons. Enquanto há termo de busca, tudo que voltou fica aberto.
 */
function expandAllForSearch() {
  tree.value.forEach((company) => {
    expandedCompanies.value.add(company.id)
    company.units.forEach((unit) => expandedUnits.value.add(unit.id))
  })
}

// ---------------------------------------------------------------------------
// Modo navegação
// ---------------------------------------------------------------------------

async function loadTree() {
  isLoading.value = true
  errorMessage.value = ''
  try {
    treeCompanies.value = await Network.schedule.listImportableTree()
  } catch (e) {
    // `e instanceof Error ? e.message : ...` vazava texto técnico de TypeError e
    // ignorava rede/timeout/5xx — o funil canônico cobre os três casos.
    errorMessage.value = getApiErrorMessage(e, 'Não foi possível carregar as escalas disponíveis.')
    treeCompanies.value = []
  } finally {
    isLoading.value = false
  }
  await refreshObserver()
}

async function loadUnitSchedules(healthUnitId: number, { append }: { append: boolean }) {
  const current = unitPages.value.get(healthUnitId)
  if (current?.isLoading) return

  const page: UnitPage = {
    items: append ? (current?.items ?? []) : [],
    count: append ? (current?.count ?? 0) : 0,
    isLoading: true,
    error: ''
  }
  unitPages.value.set(healthUnitId, page)
  // Map não é reativo por mutação: reatribuir é o que dispara o re-render.
  unitPages.value = new Map(unitPages.value)

  try {
    const response = await Network.schedule.listImportableConfigurations({
      healthUnitId,
      offset: page.items.length,
      limit: PAGE_SIZE
    })

    unitPages.value.set(healthUnitId, {
      items: [...page.items, ...response.data.map(({ id, name }) => ({ id, name }))],
      count: response.count,
      isLoading: false,
      error: ''
    })
  } catch (e) {
    unitPages.value.set(healthUnitId, {
      ...page,
      isLoading: false,
      error: getApiErrorMessage(e, 'Não foi possível carregar as escalas desta unidade.')
    })
  } finally {
    unitPages.value = new Map(unitPages.value)
    await refreshObserver()
  }
}

// ---------------------------------------------------------------------------
// Modo busca
// ---------------------------------------------------------------------------

async function fetchSearchPage({ append }: { append: boolean }) {
  if (append) {
    isLoadingMoreSearch.value = true
  } else {
    isLoading.value = true
  }
  errorMessage.value = ''

  const requestedQuery = searchQuery.value.trim()

  try {
    const response = await Network.schedule.listImportableConfigurations({
      q: requestedQuery,
      offset: append ? searchItems.value.length : 0,
      limit: PAGE_SIZE
    })

    // Descarta resposta obsoleta: o usuário pode ter digitado outra coisa enquanto
    // esta requisição estava em voo, e concatenar aqui misturaria dois resultados.
    if (requestedQuery !== searchQuery.value.trim()) return

    searchItems.value = append ? [...searchItems.value, ...response.data] : response.data
    searchCount.value = response.count
    expandAllForSearch()
  } catch (e) {
    errorMessage.value = getApiErrorMessage(e, 'Não foi possível carregar as escalas disponíveis.')
    if (!append) {
      searchItems.value = []
      searchCount.value = 0
    }
  } finally {
    isLoading.value = false
    isLoadingMoreSearch.value = false
    await refreshObserver()
  }
}

/** Recarrega o modo atual a partir do zero. */
async function reload() {
  if (isSearching.value) {
    searchItems.value = []
    searchCount.value = 0
    await fetchSearchPage({ append: false })
    return
  }
  unitPages.value = new Map()
  await loadTree()
}

// ---------------------------------------------------------------------------
// Scroll infinito
// ---------------------------------------------------------------------------

/**
 * Um único observer para todas as sentinelas: a do resultado de busca e uma por
 * unidade expandida que ainda tem página. O alvo carrega a unidade no próprio
 * atributo, então o callback não precisa de closure por unidade.
 *
 * `disconnect()` + `observe()` a cada carga é intencional: o IntersectionObserver
 * só notifica em MUDANÇA de interseção, então uma sentinela que continua visível
 * depois de uma página nunca reemitiria e a paginação travaria até o usuário rolar.
 * Reobservar força uma notificação inicial nova.
 */
async function refreshObserver() {
  disconnectObserver()
  await nextTick()

  const container = scrollContainer.value
  if (!container || typeof IntersectionObserver === 'undefined') return

  const sentinels = container.querySelectorAll<HTMLElement>('[data-load-more]')
  if (!sentinels.length) return

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const target = entry.target as HTMLElement
        const unitId = target.dataset.loadMore

        if (unitId === 'search') {
          if (isLoading.value || isLoadingMoreSearch.value || !hasMoreSearch.value) return
          fetchSearchPage({ append: true })
          return
        }

        const healthUnitId = Number(unitId)
        const page = unitPages.value.get(healthUnitId)
        if (!page || page.isLoading || page.items.length >= page.count) return
        loadUnitSchedules(healthUnitId, { append: true })
      })
    },
    { root: container, rootMargin: '80px' }
  )
  sentinels.forEach((sentinel) => observer?.observe(sentinel))
}

function disconnectObserver() {
  observer?.disconnect()
  observer = null
}

// Abrir/fechar unidade cria e destrói sentinelas, então o observer precisa ser
// reapontado quando o conjunto de expandidos muda.
watch([expandedCompanies, expandedUnits], () => refreshObserver(), { deep: true })

watch(searchQuery, () => {
  if (!props.modalActive) return
  clearTimeout(searchDebounceTimer)
  searchDebounceTimer = setTimeout(() => {
    // `resetState()` limpa o campo de busca, o que reagenda este timer enquanto o
    // modal ainda está marcado como aberto. A segunda checagem evita a requisição
    // fantasma que dispararia depois de fechar ou de importar.
    if (!props.modalActive) return
    selectedScheduleId.value = null
    expandedCompanies.value = new Set()
    expandedUnits.value = new Set()
    reload()
  }, SEARCH_DEBOUNCE_MS)
})

watch(
  () => props.modalActive,
  (active) => {
    if (!active) {
      // Cobre também o fechamento vindo do pai (import concluído), em que
      // `handleClose` não é chamado — sem isso a seleção e a busca sobreviveriam
      // para a próxima abertura.
      resetState()
      return
    }
    reload()
  },
  // `immediate` cobre o caso de o componente ser montado já com o modal aberto.
  // Sem isso, o watcher só reagiria à transição fechado → aberto e a lista ficaria
  // permanentemente vazia.
  { immediate: true }
)

function resetState() {
  clearTimeout(searchDebounceTimer)
  disconnectObserver()
  selectedScheduleId.value = null
  searchQuery.value = ''
  errorMessage.value = ''
  treeCompanies.value = []
  unitPages.value = new Map()
  searchItems.value = []
  searchCount.value = 0
  expandedCompanies.value = new Set()
  expandedUnits.value = new Set()
}

function handleClose() {
  if (props.isImporting) return
  resetState()
  emit('close')
}

/**
 * Não reseta o estado aqui: o import é assíncrono e o modal fica aberto até o pai
 * concluir. Zerar a seleção agora apagaria da tela a escala escolhida durante o
 * loading e, em caso de falha, deixaria o modal aberto sem nada selecionado.
 * A limpeza acontece quando o pai fecha o modal (watcher de `modalActive`).
 */
function handleImport() {
  if (props.isImporting) return
  const scheduleId = selectedScheduleId.value
  if (scheduleId === null) return
  const company = tree.value.find((item) =>
    item.units.some((unit) => unit.schedules.some((schedule) => schedule.id === scheduleId))
  )
  emit('select', scheduleId, company?.id ?? null)
}

onBeforeUnmount(() => {
  clearTimeout(searchDebounceTimer)
  disconnectObserver()
})
</script>
